import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Button,
    TextField,
    Paper,
    Switch,
    FormControlLabel,
    Alert,
    CircularProgress,
    InputAdornment
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { useTeam } from 'team/contexts/teamContext';
import { getTeam, updateTeam } from 'team/apis/teamApi';
import { uploadImageBlob } from 'apis/imageStorageApi';
import { useTeamAssets } from 'team/hooks/useTeamAssets';
import { TEAM_ASSET_TYPES } from 'team/apis/teamAssetApi';
import getCroppedImg from 'team/shared/utils/images/teamCropImageUtils';
import Cropper from 'react-easy-crop';
import apiClient from '../../../config/axiosConfig';
import { API_BASE_URL } from 'utils/apiUtils';

const COMMON_TIMEZONES = [
    { value: 'UTC-05:00', label: '(UTC-05:00) New York (EST)' },
    { value: 'UTC-06:00', label: '(UTC-06:00) Chicago (CST)' },
    { value: 'UTC-07:00', label: '(UTC-07:00) Denver (MST)' },
    { value: 'UTC-08:00', label: '(UTC-08:00) Los Angeles (PST)' },
    { value: 'UTC+00:00', label: '(UTC+00:00) London (GMT)' },
    { value: 'UTC+01:00', label: '(UTC+01:00) Paris (CET)' },
    { value: 'UTC+05:30', label: '(UTC+05:30) Mumbai (IST)' },
    { value: 'UTC+08:00', label: '(UTC+08:00) Singapore (SGT)' },
    { value: 'UTC+09:00', label: '(UTC+09:00) Tokyo (JST)' },
    { value: 'UTC+10:00', label: '(UTC+10:00) Sydney (AEST)' }
];

const TeamSettingsForm = () => {
    const { currentTeamId, getCurrentTeam } = useTeam();
    const team = getCurrentTeam();
    const teamId = team?._id || team?.id || currentTeamId;

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);
    const [exporting, setExporting] = useState(false);

    const [companyName, setCompanyName] = useState('');
    const [customSubdomain, setCustomSubdomain] = useState('');
    const [domainName, setDomainName] = useState('');
    const [removeBranding, setRemoveBranding] = useState(false);
    const [enforceSSO, setEnforceSSO] = useState(false);
    const [autoAddSameDomain, setAutoAddSameDomain] = useState(false);
    const [subteamAdminManagement, setSubteamAdminManagement] = useState(false);
    const [insightsTimezone, setInsightsTimezone] = useState('UTC-05:00');
    const [gdprSensitivity, setGdprSensitivity] = useState(false);
    const [requireEmailForLeads, setRequireEmailForLeads] = useState(false);
    const [automaticLeadFollowUps, setAutomaticLeadFollowUps] = useState(true);
    const [allowMobileCampaignAssign, setAllowMobileCampaignAssign] = useState(false);
    const [requireCampaignForLeads, setRequireCampaignForLeads] = useState(false);

    const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
    const [logoCropOpen, setLogoCropOpen] = useState(false);
    const [logoImage, setLogoImage] = useState(null);
    const [logoCrop, setLogoCrop] = useState({ x: 0, y: 0 });
    const [logoZoom, setLogoZoom] = useState(1);
    const [logoCroppedAreaPixels, setLogoCroppedAreaPixels] = useState(null);
    const logoObjectUrlRef = useRef(null);

    const logoAssets = useTeamAssets(teamId, TEAM_ASSET_TYPES.LOGO);
    const fileInputRef = useRef(null);

    const settings = team?.settings || {};
    const isMap = settings instanceof Map;
    const getSetting = (key, defaultValue) => {
        if (isMap) return settings.get?.(key) ?? defaultValue;
        return settings[key] ?? defaultValue;
    };

    const loadTeamData = useCallback(async () => {
        if (!teamId) return;
        try {
            setLoading(true);
            const response = await getTeam(teamId);
            const t = response?.team || response;
            const s = t?.settings || {};
            const isSettingsMap = s instanceof Map;
            const get = (k, d) => (isSettingsMap ? s.get?.(k) ?? d : s[k] ?? d);

            setCompanyName(get('companyName', '') || t?.name || '');
            setCustomSubdomain(get('customSubdomain', ''));
            setDomainName(get('domainName', ''));
            setRemoveBranding(get('removeBranding', false));
            setEnforceSSO(get('enforceSSO', false));
            setAutoAddSameDomain(get('autoAddSameDomain', false));
            setSubteamAdminManagement(get('subteamAdminManagement', false));
            setInsightsTimezone(get('insightsTimezone', 'UTC-05:00'));
            setGdprSensitivity(get('gdprSensitivity', false));
            setRequireEmailForLeads(get('requireEmailForLeads', false));
            setAutomaticLeadFollowUps(get('automaticLeadFollowUps', true));
            setAllowMobileCampaignAssign(get('allowMobileCampaignAssign', false));
            setRequireCampaignForLeads(get('requireCampaignForLeads', false));
            const logoUrl = get('companyLogoUrl', null);
            setCompanyLogoUrl(logoUrl || null);
        } catch (error) {
            console.error('Error loading team settings:', error);
            setMessage({ text: 'Failed to load team settings', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [teamId]);

    useEffect(() => {
        loadTeamData();
    }, [loadTeamData]);

    useEffect(() => {
        if (!companyLogoUrl && logoAssets?.assets?.length > 0) {
            const firstLogo = logoAssets.assets[0];
            if (firstLogo?.url) setCompanyLogoUrl(firstLogo.url);
        }
    }, [logoAssets?.assets]);

    const handleSave = async () => {
        if (!teamId) return;
        try {
            setSaving(true);
            setMessage(null);
            const newSettings = {
                companyName,
                customSubdomain,
                domainName,
                removeBranding,
                enforceSSO,
                autoAddSameDomain,
                subteamAdminManagement,
                insightsTimezone,
                gdprSensitivity,
                requireEmailForLeads,
                automaticLeadFollowUps,
                allowMobileCampaignAssign,
                requireCampaignForLeads,
                companyLogoUrl: companyLogoUrl || undefined
            };

            await updateTeam(teamId, { settings: newSettings });
            setMessage({ text: 'Team settings saved successfully', severity: 'success' });
            loadTeamData();
        } catch (error) {
            setMessage({ text: error.message || 'Failed to save settings', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleExportTeamData = async () => {
        if (!teamId) return;
        try {
            setExporting(true);
            setMessage(null);
            const response = await apiClient.get(`${API_BASE_URL}/api/team/${teamId}/export`, {
                responseType: 'blob'
            });
            if (response.status !== 200) {
                const text = await response.data.text();
                let err = {};
                try {
                    err = JSON.parse(text || '{}');
                } catch {
                    err = { message: 'Export failed' };
                }
                throw new Error(err.message || err.error || 'Export failed');
            }
            const blob = new Blob([response.data], { type: 'application/zip' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `intouchwe-team-data-${new Date().toISOString().split('T')[0]}.zip`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setMessage({ text: 'Team data exported successfully', severity: 'success' });
        } catch (error) {
            setMessage({
                text: error.response?.data?.message || error.message || 'Failed to export team data',
                severity: 'error'
            });
        } finally {
            setExporting(false);
        }
    };

    const handleLogoFileChange = (e) => {
        const file = e?.target?.files?.[0];
        if (file) {
            if (logoObjectUrlRef.current) URL.revokeObjectURL(logoObjectUrlRef.current);
            const url = URL.createObjectURL(file);
            logoObjectUrlRef.current = url;
            setLogoImage(url);
            setLogoCropOpen(true);
        }
        e.target.value = '';
    };

    const handleLogoCropComplete = useCallback((_, pixels) => {
        setLogoCroppedAreaPixels(pixels);
    }, []);

    const settingsToPlainObject = (s) => {
        if (!s) return {};
        if (s instanceof Map) return Object.fromEntries(s.entries());
        return typeof s === 'object' ? s : {};
    };

    const handleLogoSave = async () => {
        if (!logoImage || !logoCroppedAreaPixels || !teamId) return;
        try {
            const blob = await getCroppedImg(logoImage, logoCroppedAreaPixels, 'circle', `logo-${teamId}`);
            const storageId = `${teamId}-company-logo-${Date.now()}`;
            const fileUrl = await uploadImageBlob(blob, null, 'team', 'logo', storageId);
            setCompanyLogoUrl(fileUrl);
            const currentSettings = settingsToPlainObject(team?.settings || settings);
            await updateTeam(teamId, { settings: { ...currentSettings, companyLogoUrl: fileUrl } });
            setLogoCropOpen(false);
            setLogoImage(null);
            logoAssets?.refreshAssets?.();
            setMessage({ text: 'Company logo updated', severity: 'success' });
        } catch (error) {
            setMessage({ text: error.message || 'Failed to upload logo', severity: 'error' });
        }
    };

    const handleRemoveLogo = async () => {
        setCompanyLogoUrl(null);
        const currentSettings = settingsToPlainObject(team?.settings || settings);
        await updateTeam(teamId, { settings: { ...currentSettings, companyLogoUrl: null } });
        setMessage({ text: 'Company logo removed', severity: 'success' });
    };

    const emailDomain = companyName || team?.name
        ? `@${(companyName || team?.name || '').toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '')}.com`
        : '@yourcompany.com';

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 3 }}>
            {message && (
                <Alert severity={message.severity} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            {/* Export Team Data */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, backgroundColor: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                    Export team data
                </Typography>
                <Button
                    variant="outlined"
                    startIcon={<CloudDownloadOutlinedIcon />}
                    onClick={handleExportTeamData}
                    disabled={exporting}
                >
                    {exporting ? 'Exporting...' : 'Export'}
                </Button>
            </Paper>

            {/* Company Logo */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, backgroundColor: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                    Company logo
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Set your company logo to replace the IntouchWe logo in the top left corner
                </Typography>
                <Box
                    sx={{
                        border: '2px dashed #ddd',
                        borderRadius: 2,
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 2
                    }}
                >
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleLogoFileChange}
                    />
                    {companyLogoUrl ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                                component="img"
                                src={companyLogoUrl}
                                alt="Company logo"
                                sx={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover' }}
                            />
                            <Box>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={() => fileInputRef.current?.click()}
                                    startIcon={<FileUploadOutlinedIcon />}
                                >
                                    Upload photo
                                </Button>
                                <Button size="small" color="error" onClick={handleRemoveLogo} sx={{ ml: 1 }}>
                                    Remove
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary">
                                Select file or drag and drop one here
                            </Typography>
                            <Button
                                variant="outlined"
                                startIcon={<FileUploadOutlinedIcon />}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Upload photo
                            </Button>
                        </>
                    )}
                </Box>
            </Paper>

            {/* Company Name & Domain */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, backgroundColor: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                    Company name
                </Typography>
                <TextField
                    fullWidth
                    placeholder="Enter company name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    variant="outlined"
                    size="small"
                />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mt: 3, mb: 2 }}>
                    Custom subdomain
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                    Setting this changes how you & your team will access IntouchWe Dashboard and member cards
                </Typography>
                <TextField
                    fullWidth
                    placeholder="your-subdomain"
                    value={customSubdomain || domainName}
                    onChange={(e) => {
                        const val = e.target.value.replace(/[^a-z0-9-]/gi, '').toLowerCase();
                        setCustomSubdomain(val);
                        setDomainName(val);
                    }}
                    variant="outlined"
                    size="small"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">.intouchwe.com</InputAdornment>
                        )
                    }}
                />
            </Paper>

            {/* Branding & SSO */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, backgroundColor: 'white' }}>
                <FormControlLabel
                    control={<Switch checked={removeBranding} onChange={(e) => setRemoveBranding(e.target.checked)} />}
                    label={
                        <Box>
                            <Typography variant="body1" fontWeight={500}>Remove IntouchWe Branding</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Remove IntouchWe branding for existing and future members
                            </Typography>
                        </Box>
                    }
                />
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={<Switch checked={enforceSSO} onChange={(e) => setEnforceSSO(e.target.checked)} />}
                        label={
                            <Box>
                                <Typography variant="body1" fontWeight={500}>Enforce Logging in with SSO</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Turn this option on to force members on your team to login via SSO, Microsoft, or
                                    Google, thereby requiring MFA
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch checked={autoAddSameDomain} onChange={(e) => setAutoAddSameDomain(e.target.checked)} />
                        }
                        label={
                            <Box>
                                <Typography variant="body1" fontWeight={500}>
                                    Auto add members with the same email domain to team
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Turning on this option will automatically add members to your team when they login
                                    with your organization&apos;s SSO
                                    <Typography component="span" sx={{ fontWeight: 600, ml: 0.5 }}>
                                        {emailDomain}
                                    </Typography>
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={subteamAdminManagement}
                                onChange={(e) => setSubteamAdminManagement(e.target.checked)}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body1" fontWeight={500}>Subteam admin management</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Turn on to allow subteam admins to add and remove members from their subteam(s)
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
            </Paper>

            {/* Insights & GDPR */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, backgroundColor: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                    Insights Page Timezone
                </Typography>
                <TextField
                    select
                    fullWidth
                    value={insightsTimezone}
                    onChange={(e) => setInsightsTimezone(e.target.value)}
                    variant="outlined"
                    size="small"
                    SelectProps={{ native: true }}
                >
                    {COMMON_TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                            {tz.label}
                        </option>
                    ))}
                </TextField>
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch checked={gdprSensitivity} onChange={(e) => setGdprSensitivity(e.target.checked)} />
                        }
                        label={
                            <Box>
                                <Typography variant="body1" fontWeight={500}>GDPR Sensitivity</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Enabling GDPR mode ensures lead data collected via IntouchWe complies with GDPR
                                    regulations.
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
            </Paper>

            {/* Lead Collection */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, backgroundColor: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                    Lead collection
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={requireEmailForLeads}
                            onChange={(e) => setRequireEmailForLeads(e.target.checked)}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body1" fontWeight={500}>
                                Require email address when collecting leads
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Turn on to require an email when collecting leads via scans or manual entry. If enabled,
                                mobile app users will not be able to save a lead without an email.
                            </Typography>
                        </Box>
                    }
                />
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={automaticLeadFollowUps}
                                onChange={(e) => setAutomaticLeadFollowUps(e.target.checked)}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body1" fontWeight={500}>Automatic Lead Follow Ups</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Automatic email to the lead with a link to the digital card of the IntouchWe user who
                                    captured that lead. This does not send if a custom follow up email is sent via a
                                    Campaign or Template.
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
            </Paper>

            {/* Mobile App Campaign Management */}
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2, backgroundColor: 'white' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
                    Mobile App Campaign Management
                </Typography>
                <FormControlLabel
                    control={
                        <Switch
                            checked={allowMobileCampaignAssign}
                            onChange={(e) => setAllowMobileCampaignAssign(e.target.checked)}
                        />
                    }
                    label={
                        <Box>
                            <Typography variant="body1" fontWeight={500}>
                                Allow mobile app users to assign themselves and their leads to any Campaign
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Take the work off you as the admin and allow your team members to assign themselves and
                                their leads to any Campaign you&apos;ve created.
                            </Typography>
                        </Box>
                    }
                />
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={requireCampaignForLeads}
                                onChange={(e) => setRequireCampaignForLeads(e.target.checked)}
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="body1" fontWeight={500}>
                                    Require a Campaign selection for every lead
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    If enabled, leads must be assigned to a Campaign to be saved. Make sure your team has
                                    a Campaign for every lead they plan to capture.
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
            </Paper>

            {/* Save Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="contained" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                </Button>
            </Box>

            {/* Logo Crop Modal */}
            {logoCropOpen && logoImage && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.9)',
                        zIndex: 9999,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <Box sx={{ width: '90%', maxWidth: 400, height: 400 }}>
                        <Cropper
                            image={logoImage}
                            crop={logoCrop}
                            zoom={logoZoom}
                            aspect={1}
                            cropShape="round"
                            onCropChange={setLogoCrop}
                            onZoomChange={setLogoZoom}
                            onCropComplete={handleLogoCropComplete}
                        />
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                        <Button variant="outlined" onClick={() => setLogoCropOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleLogoSave}>
                            Save
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default TeamSettingsForm;
